import React from "react";
import PropTypes from "prop-types";
import { Row, Col } from "react-flexbox-grid/lib/index";
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  IconButton,
  CircularProgress,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Tooltip
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import Visibility from "@material-ui/icons/Visibility";
import CastDownload from "@material-ui/icons/CloudDownload";
import InsertChart from "@material-ui/icons/InsertChart";
import BarChart from "@material-ui/icons/BarChart";
import moment from "moment";

const styles = theme => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
    overflowX: "auto"
  },
  table: {
    minWidth: 700
  }
});

const processedStages = [
  "8_rbr_processed",
  "8_binAvg",
  "9_qc_auto",
  "10_qc_pi"
];

const CastsResultCard = props => {
  const { classes } = props;
  return (
    <Card>
      <CardHeader title="Results" />
      <CardContent>
        <Row>
          <Col xs>
            <Chip label={`${props.totalCastsWithFilters} records found`} />
          </Col>
          <Col s="true">
            <Tooltip title="Opens visualization of results">
              <div>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={!props.totalCastsWithFilters}
                  onClick={() => props.onCreateCastDistChartClick()}
                >
                  Preview Cast Distribution&nbsp;&nbsp;
                  <BarChart color="inherit" />
                </Button>
              </div>
            </Tooltip>
          </Col>
          <br />
          <Col s="true">
            <Tooltip title="Must be xlsx or csv">
              <div>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={
                    props.metadataDownloadIsFetching ||
                    props.downloadType === "file" ||
                    props.downloadType === "hexrsk" ||
                    !props.totalCastsWithFilters
                  }
                  onClick={() =>
                    props.onDownloadMetadataClick(props.downloadType)
                  }
                  style={{ marginLeft: 10, marginRight: 10 }}
                >
                  {props.metadataDownloadIsFetching
                    ? "Downloading..."
                    : "Download All Metadata"}
                  &nbsp;&nbsp;
                  <CastDownload color="inherit" />
                </Button>
              </div>
            </Tooltip>
          </Col>
          <Col s="true">
            <Button
              variant="contained"
              color="secondary"
              disabled={
                props.bulkDownloadIsFetching || !props.totalCastsWithFilters
              }
              onClick={() => props.onDownloadAllClick(props.downloadType)}
              style={{
                marginLeft: 10,
                marginRight: 10,
                color: props.totalCastsWithFilters
                  ? "#fff"
                  : "rgba(0, 0, 0, 0.12)"
              }}
            >
              {props.bulkDownloadIsFetching
                ? "Downloading..."
                : "Download All Data"}
              &nbsp;&nbsp;
              <CastDownload color="inherit" />
            </Button>
          </Col>
        </Row>
        <Row style={{ marginTop: "1rem" }}>
          <Col xs={12} className={classes.root}>
            {/* Paginated browsing table */}
            {props.isFetching ? (
              <LinearProgress />
            ) : (
              <Table
                className={classes.table}
                style={{ width: "100%", tableLayout: "auto" }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Filename</TableCell>
                    <TableCell>Cruise</TableCell>
                    <TableCell>Station</TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      Cast Date
                    </TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      Cast Time
                    </TableCell>
                    <TableCell>Drop</TableCell>
                    <TableCell>Preview</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell>PDF</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {props.casts.map(f => (
                    <TableRow key={f.data.ctd_cast_pk}>
                      <TableCell title={f.name}>{f.name}</TableCell>
                      <TableCell title={f.data.cruise}>
                        {f.data.cruise}
                      </TableCell>
                      <TableCell title={f.data.station}>
                        {f.data.station}
                      </TableCell>
                      <TableCell
                        title={f.data.start_dt}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {moment(f.data.start_dt).format("YYYY-MM-DD")}
                      </TableCell>
                      <TableCell
                        title={f.data.start_dt}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {moment(f.data.start_dt).format("LT")}
                      </TableCell>
                      <TableCell title={f.data.cast_number}>
                        {f.data.cast_number}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          title="view"
                          onClick={() =>
                            props.onViewClick(props.downloadType, f)
                          }
                          disabled={props.downloadType === "xlsx"}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          title="download"
                          onClick={() =>
                            props.onDownloadClick(props.downloadType, f)
                          }
                        >
                          {f.contentIsFetching ? (
                            <CircularProgress
                              size={20}
                              color="primary"
                              thickness={7}
                            />
                          ) : (
                            <CastDownload />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell style={{ width: 10, textAlign: "left" }}>
                        <IconButton
                          title="RBR PDF chart"
                          disabled={
                            !processedStages.includes(f.data.processing_stage)
                          }
                          onClick={() => {
                            props.onDownloadPDFClick(f);
                          }}
                        >
                          {f.pdfContentIsFetching ? (
                            <CircularProgress
                              size={20}
                              color="primary"
                              thickness={7}
                            />
                          ) : (
                            <InsertChart />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Col>
        </Row>
      </CardContent>
    </Card>
  );
};

CastsResultCard.propTypes = {
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    table: PropTypes.string.isRequired
  }),
  casts: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      data: PropTypes.shape({
        ctd_cast_pk: PropTypes.number.isRequired,
        ctd_file_pk: PropTypes.number.isRequired,
        cruise: PropTypes.string,
        station: PropTypes.string,
        start_dt: PropTypes.string.isRequired,
        filename: PropTypes.string.isRequired,
        workarea: PropTypes.string,
        processing_stage: PropTypes.string.isRequired
      }),
      contentIsFetching: PropTypes.bool.isRequired,
      pdfContentIsFetching: PropTypes.bool.isRequired,
      isWirewalker: PropTypes.bool.isRequired,
      selected: PropTypes.string.isRequired
    })
  ),
  isFetching: PropTypes.bool.isRequired,

  downloadType: PropTypes.string.isRequired,
  totalCastsWithFilters: PropTypes.number,
  bulkDownloadIsFetching: PropTypes.bool.isRequired,
  metadataDownloadIsFetching: PropTypes.bool.isRequired,

  onDownloadClick: PropTypes.func.isRequired,
  onDownloadAllClick: PropTypes.func.isRequired,
  onDownloadMetadataClick: PropTypes.func.isRequired,
  onDownloadPDFClick: PropTypes.func.isRequired,
  onViewClick: PropTypes.func.isRequired,
  onCreateCastDistChartClick: PropTypes.func.isRequired
};

CastsResultCard.defaultProps = {
  classes: {},
  casts: [],
  totalCastsWithFilters: 0
};

export default withStyles(styles)(CastsResultCard);
